import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../contexts/AdminContext';
import { toast } from 'sonner';
import { MessageCircle, Send, Trash2, Loader2, CornerDownRight, X } from 'lucide-react';
import UserAvatar from './ui/UserAvatar';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ComentariosSection — Reusable comments section for Academia content.
 * Suporta RESPOSTAS entre usuários (1 nível, estilo YouTube): responder uma
 * resposta vira resposta ao comentário raiz (o backend achata via parent_id).
 *
 * Props:
 *   conteudoTipo: 'curso' | 'treinamento' | 'recurso' | 'aula'
 *   conteudoId: string (UUID)
 */
const ComentariosSection = ({ conteudoTipo, conteudoId }) => {
  const { t } = useTranslation();
  const { isSuperAdmin } = useAdmin();

  const [comentarios, setComentarios] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [texto, setTexto] = useState('');
  const [sending, setSending] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  // Resposta inline: { targetId (comentário clicado — vira a menção @Nome),
  //   rootId (thread onde a resposta entra), nome }
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const displayName = (user) =>
    user?.full_name ||
    (user?.username?.startsWith('USER_') ? user?.email?.split('@')[0] : user?.username) ||
    '?';

  // Fetch comments
  const fetchComentarios = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const res = await axios.get(
        `${API}/academia/comentarios/${conteudoTipo}/${conteudoId}?page=${pageNum}&limit=10`
      );
      const data = res.data;

      if (append) {
        setComentarios(prev => [...prev, ...data.comentarios]);
      } else {
        setComentarios(data.comentarios);
      }
      setTotal(data.total);
      setHasMore(data.has_more);
      setPage(pageNum);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [conteudoTipo, conteudoId]);

  // Load on mount or when content changes
  useEffect(() => {
    if (conteudoTipo && conteudoId) {
      setComentarios([]);
      setPage(1);
      setReplyingTo(null);
      setReplyText('');
      fetchComentarios(1);
    }
  }, [conteudoTipo, conteudoId, fetchComentarios]);

  // Send root comment
  const handleSend = async () => {
    const trimmed = texto.trim();
    if (!trimmed || sending) return;
    if (trimmed.length > 2000) {
      toast.error('Max 2000 caracteres');
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/academia/comentarios`, {
        conteudo_tipo: conteudoTipo,
        conteudo_id: conteudoId,
        texto: trimmed,
      });
      // Prepend new comment to list
      setComentarios(prev => [{ ...res.data, replies: res.data.replies || [] }, ...prev]);
      setTotal(prev => prev + 1);
      setTexto('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar comentário');
    } finally {
      setSending(false);
    }
  };

  // Send reply (parent = comentário RAIZ)
  const handleSendReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed || sendingReply || !replyingTo) return;
    if (trimmed.length > 2000) {
      toast.error('Max 2000 caracteres');
      return;
    }

    setSendingReply(true);
    try {
      const res = await axios.post(`${API}/academia/comentarios`, {
        conteudo_tipo: conteudoTipo,
        conteudo_id: conteudoId,
        texto: trimmed,
        // id do comentário CLICADO: o backend achata o thread pro raiz,
        // mas grava a menção @ pro autor deste comentário
        parent_id: replyingTo.targetId,
      });
      setComentarios(prev => prev.map(c =>
        c.id === replyingTo.rootId
          ? { ...c, replies: [...(c.replies || []), res.data] }
          : c
      ));
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar resposta');
    } finally {
      setSendingReply(false);
    }
  };

  // Delete comment (raiz remove o bloco inteiro; resposta sai do pai)
  const handleDelete = async (id, isReply, rootId) => {
    if (!window.confirm(t('academia.comments.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/academia/comentarios/${id}`);
      if (isReply) {
        setComentarios(prev => prev.map(c =>
          c.id === rootId
            ? { ...c, replies: (c.replies || []).filter(r => r.id !== id) }
            : c
        ));
      } else {
        setComentarios(prev => prev.filter(c => c.id !== id));
        setTotal(prev => prev - 1);
      }
      toast.success(t('academia.comments.delete'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao deletar');
    }
  };

  // Relative time
  const timeAgo = (dateStr) => {
    if (!dateStr) return t('academia.comments.justNow');
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    const diffM = Math.floor(diffD / 30);

    if (diffMin < 1) return t('academia.comments.justNow');
    if (diffMin < 60) return t('academia.comments.minutesAgo', { count: diffMin });
    if (diffH < 24) return t('academia.comments.hoursAgo', { count: diffH });
    if (diffD < 30) return t('academia.comments.daysAgo', { count: diffD });
    return t('academia.comments.monthsAgo', { count: diffM });
  };

  // Bloco de um comentário (raiz ou resposta)
  const renderComentario = (c, { isReply = false, rootId = null } = {}) => (
    <div
      key={c.id}
      onMouseEnter={() => setHoveredId(c.id)}
      onMouseLeave={() => setHoveredId(null)}
      style={{
        display: 'flex', gap: '10px', alignItems: 'flex-start',
        padding: isReply ? '4px 0' : '8px 0',
      }}
    >
      <UserAvatar
        avatarUrl={c.user?.avatar_url}
        name={displayName(c.user)}
        size={isReply ? 28 : 36}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name + time + actions */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          marginBottom: '3px',
        }}>
          <span style={{
            fontSize: '13px', fontWeight: 600,
            color: 'var(--dp-text-primary)',
          }}>
            {displayName(c.user)}
          </span>
          <span style={{
            fontSize: '11px', color: 'var(--dp-text-tertiary)',
          }}>
            {timeAgo(c.created_at)}
          </span>
          {/* Delete button (owner or admin, shown on hover) */}
          {(c.is_owner || isSuperAdmin) && hoveredId === c.id && (
            <button
              onClick={() => handleDelete(c.id, isReply, rootId)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--dp-text-tertiary)', padding: '2px',
                marginLeft: 'auto', display: 'flex',
                alignItems: 'center', transition: 'color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#EF4444'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
              title={t('academia.comments.delete')}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        {/* Comment text (respostas levam a menção @Nome, estilo rede social) */}
        <p style={{
          fontSize: '13px', color: 'var(--dp-text-secondary)',
          lineHeight: 1.5, margin: 0,
          wordBreak: 'break-word', whiteSpace: 'pre-wrap',
        }}>
          {c.reply_to && (
            <span style={{ color: 'var(--dp-blue-500)', fontWeight: 600, marginRight: '5px' }}>
              @{displayName(c.reply_to)}
            </span>
          )}
          {c.texto}
        </p>
        {/* Responder (respostas também: vira resposta ao mesmo raiz) */}
        <button
          onClick={() => {
            const root = isReply ? rootId : c.id;
            setReplyingTo(prev => (prev?.targetId === c.id)
              ? null
              : { targetId: c.id, rootId: root, nome: displayName(c.user) });
            setReplyText('');
          }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0',
            marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px',
            color: 'var(--dp-text-tertiary)', fontSize: '11.5px', fontWeight: 600,
            fontFamily: 'var(--dp-font)', transition: 'color 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
        >
          <CornerDownRight size={12} />
          {t('academia.comments.reply') || 'Responder'}
        </button>
      </div>
    </div>
  );

  // Caixa de resposta inline (aparece sob o bloco do comentário raiz)
  const renderReplyBox = (rootId) => (
    replyingTo?.rootId === rootId && (
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'center',
        marginLeft: '46px', marginTop: '2px', marginBottom: '6px',
        background: 'var(--dp-bg-input, rgba(255,255,255,0.04))',
        border: '1px solid var(--dp-border-subtle)',
        borderRadius: '10px', padding: '6px 10px',
      }}>
        <CornerDownRight size={13} style={{ color: 'var(--dp-blue-500)', flexShrink: 0 }} />
        <input
          autoFocus
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); }
            if (e.key === 'Escape') { setReplyingTo(null); setReplyText(''); }
          }}
          placeholder={(t('academia.comments.replyTo') || 'Responder a {{name}}...').replace('{{name}}', replyingTo.nome)}
          maxLength={2000}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--dp-text-primary)', fontSize: '13px',
            fontFamily: 'var(--dp-font)',
          }}
        />
        <button
          onClick={() => { setReplyingTo(null); setReplyText(''); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
            color: 'var(--dp-text-tertiary)', display: 'flex', flexShrink: 0,
          }}
          title={t('channels.cancel') || 'Cancelar'}
        >
          <X size={14} />
        </button>
        <button
          onClick={handleSendReply}
          disabled={!replyText.trim() || sendingReply}
          style={{
            background: replyText.trim() ? 'var(--dp-blue-500, #0DAEFF)' : 'transparent',
            border: 'none', borderRadius: '8px',
            width: '28px', height: '28px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: replyText.trim() && !sendingReply ? 'pointer' : 'default',
            color: replyText.trim() ? '#fff' : 'var(--dp-text-tertiary)',
            transition: 'all 0.2s', flexShrink: 0,
            opacity: replyText.trim() ? 1 : 0.5,
          }}
        >
          {sendingReply ? (
            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Send size={14} />
          )}
        </button>
      </div>
    )
  );

  return (
    <div style={{
      marginTop: '24px',
      borderTop: '1px solid var(--dp-border-subtle)',
      paddingTop: '20px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '16px',
      }}>
        <MessageCircle size={18} style={{ color: 'var(--dp-blue-500)' }} />
        <span style={{
          fontSize: '15px', fontWeight: 600,
          color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
        }}>
          {t('academia.comments.title')} ({total})
        </span>
      </div>

      {/* Input area */}
      <div style={{
        display: 'flex', gap: '10px', alignItems: 'flex-start',
        marginBottom: '20px',
      }}>
        <div style={{
          flex: 1, display: 'flex', gap: '8px', alignItems: 'center',
          background: 'var(--dp-bg-input, rgba(255,255,255,0.04))',
          border: '1px solid var(--dp-border-subtle)',
          borderRadius: '10px', padding: '8px 12px',
          transition: 'border-color 0.2s',
        }}>
          <input
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={t('academia.comments.placeholder')}
            maxLength={2000}
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--dp-text-primary)', fontSize: '13px',
              fontFamily: 'var(--dp-font)',
            }}
          />
          <button
            onClick={handleSend}
            disabled={!texto.trim() || sending}
            style={{
              background: texto.trim() ? 'var(--dp-blue-500, #0DAEFF)' : 'transparent',
              border: 'none', borderRadius: '8px',
              width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: texto.trim() && !sending ? 'pointer' : 'default',
              color: texto.trim() ? '#fff' : 'var(--dp-text-tertiary)',
              transition: 'all 0.2s', flexShrink: 0,
              opacity: texto.trim() ? 1 : 0.5,
            }}
          >
            {sending ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div className="dp-skeleton" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="dp-skeleton" style={{ width: '120px', height: '12px', marginBottom: '6px' }} />
                <div className="dp-skeleton" style={{ width: '80%', height: '12px' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && comentarios.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '24px 16px',
          color: 'var(--dp-text-tertiary)', fontSize: '13px',
        }}>
          <MessageCircle size={28} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
          <p style={{ margin: 0 }}>{t('academia.comments.empty')}</p>
        </div>
      )}

      {/* Comments list */}
      {!loading && comentarios.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {comentarios.map(c => (
            <div key={c.id}>
              {renderComentario(c)}

              {/* Respostas aninhadas */}
              {(c.replies || []).length > 0 && (
                <div style={{
                  marginLeft: '22px', paddingLeft: '20px',
                  borderLeft: '2px solid var(--dp-border-subtle)',
                  display: 'flex', flexDirection: 'column', gap: '2px',
                }}>
                  {c.replies.map(r => renderComentario(r, { isReply: true, rootId: c.id }))}
                </div>
              )}

              {renderReplyBox(c.id)}
            </div>
          ))}

          {/* Load more */}
          {hasMore && (
            <button
              onClick={() => fetchComentarios(page + 1, true)}
              disabled={loadingMore}
              style={{
                background: 'none', border: '1px solid var(--dp-border-subtle)',
                borderRadius: '8px', padding: '8px 16px',
                color: 'var(--dp-blue-500)', fontSize: '13px', fontWeight: 500,
                fontFamily: 'var(--dp-font)', cursor: loadingMore ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '6px', width: '100%', marginTop: '4px',
                transition: 'all 0.2s',
              }}
            >
              {loadingMore ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              {loadingMore ? t('academia.comments.sending') : t('academia.comments.loadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ComentariosSection;
